/**
 * The line a meeting leaves behind in the room.
 *
 * The server writes an ordinary message from whoever rang, with
 * `client_message_id` set to `call:<id>`. The client recognises that prefix
 * and draws it as a small card — not a new message type, not a block format,
 * nothing the timeline has to learn. A client that does not recognise it
 * still shows the sentence, which is why the sentence has to read on its own.
 */

import type { ReactElement } from 'react'

import type { Message } from '@/api/types'
import { Button } from '@/components/ui'
import styles from './CallWindow.module.css'

const CALL_PREFIX = 'call:'

/** Whether this message is a call's own record of itself. */
export function isCallLog(message: Pick<Message, 'client_message_id' | 'text'>): boolean {
  return message.client_message_id?.startsWith(CALL_PREFIX) === true && message.text.startsWith('📞')
}

/** Nobody picked up. Only these get an offer to try again. */
export function isMissedCall(text: string): boolean {
  return text.includes('no answer') || text.includes('cancelled')
}

function CameraIcon(): ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.6" y="5.2" width="10.4" height="9.6" rx="2.2" />
      <path d="M13 9.4 17.4 6.8v6.4L13 10.6z" />
    </svg>
  )
}

function MissedIcon(): ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4.2 3.6h3l1.4 3.4-1.9 1.4a9.4 9.4 0 0 0 4.5 4.5l1.4-1.9 3.4 1.4v3a1.4 1.4 0 0 1-1.5 1.4A13.4 13.4 0 0 1 2.8 5.1a1.4 1.4 0 0 1 1.4-1.5z" />
      <path d="M14.2 3.4 17.6 6.8M17.6 3.4l-3.4 3.4" />
    </svg>
  )
}

export function CallLogMessage({
  text,
  /** Offered on unanswered calls, and only where calling back means one person. */
  onCallBack,
}: {
  text: string
  onCallBack?: () => void
}): ReactElement {
  const missed = isMissedCall(text)
  // The server writes the telephone as the first character; the icon says it
  // better than an emoji does at this size.
  const line = text.replace(/^📞\s*/u, '')

  return (
    <span className={styles.logCard}>
      <span className={styles.logIcon} data-missed={missed} aria-hidden="true">
        {missed ? <MissedIcon /> : <CameraIcon />}
      </span>
      {line}
      {missed && onCallBack !== undefined ? (
        <Button size="sm" variant="ghost" onClick={onCallBack}>
          Call back
        </Button>
      ) : null}
    </span>
  )
}
